import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';

import { agentWorkbenchNotifyOnRunCompleteAtom } from '#state/atoms/agent-workbench-notify-on-run-complete-atom.js';
import { getRunNotificationPermission, requestRunNotificationPermission } from './run-complete-notification.js';

import type { ReactElement } from 'react';

/**
 * Bell control that turns the run-completion notification on and off. Turning it
 * on prompts the browser for permission, and the control settles into a disabled
 * blocked state if the user refuses. Renders nothing where notifications are
 * unsupported.
 */
export function AgentWorkbenchNotificationToggle(): ReactElement | null {
	const [isEnabled, setIsEnabled] = useAtom(agentWorkbenchNotifyOnRunCompleteAtom);
	const [permission, setPermission] = useState(getRunNotificationPermission);

	// Permission is revoked and granted in browser chrome, which fires no event we
	// can subscribe to. Re-reading it whenever the tab comes back is enough to stop
	// the control claiming notifications are on after they were switched off, or
	// staying disabled after the user relented.
	useEffect(() => {
		const syncPermission = (): void => {
			if (document.visibilityState === 'visible') {
				setPermission(getRunNotificationPermission());
			}
		};
		document.addEventListener('visibilitychange', syncPermission);
		return () => document.removeEventListener('visibilitychange', syncPermission);
	}, []);

	if (permission === undefined) {
		return null;
	}

	const isBlocked = permission === 'denied';
	// Permission revoked out from under an enabled preference: report the truth.
	const isOn = isEnabled && permission === 'granted';

	const toggle = async (): Promise<void> => {
		if (isOn) {
			setIsEnabled(false);
			return;
		}
		const granted = await requestRunNotificationPermission();
		setPermission(granted);
		setIsEnabled(granted === 'granted');
	};

	const label =
		isBlocked ? 'Notifications are blocked in your browser settings'
		: isOn ? 'Notify me when an agent finishes: on'
		: 'Notify me when an agent finishes: off';

	return (
		<Tooltip title={label}>
			{/* Wrapper keeps the tooltip working while the button is disabled. */}
			<span className='flex'>
				<ButtonBase
					aria-label={label}
					aria-pressed={isOn}
					disabled={isBlocked}
					onClick={() => void toggle()}
					className={clsx(
						'flex items-center justify-center rounded p-1 transition-colors duration-150 motion-reduce:transition-none',
						isBlocked ? 'text-gray-a6'
						: isOn ? 'text-blue-11 hover:bg-blue-a3'
						: 'text-gray-11 hover:bg-gray-a3 hover:text-gray-12',
					)}
				>
					{isOn ?
						<NotificationsOutlinedIcon style={{ fontSize: 18 }} />
					:	<NotificationsOffOutlinedIcon style={{ fontSize: 18 }} />}
				</ButtonBase>
			</span>
		</Tooltip>
	);
}
